import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Globe2, Radio, Megaphone, Swords, Layers } from 'lucide-react';
interface SlidePressureProps {
  onContinue: () => void;
}
const PRESSURES = [{
  label: 'More brands',
  icon: Layers
}, {
  label: 'More markets',
  icon: Globe2
}, {
  label: 'More channels',
  icon: Radio
}, {
  label: 'More campaigns',
  icon: Megaphone
}, {
  label: 'More competition',
  icon: Swords
}] as const;
// Rising staircase offsets (px) — each step sits higher than the last
const LIFT = [0, 18, 36, 54, 74];
export function SlidePressure({
  onContinue
}: SlidePressureProps) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const timers = [300, 650, 1000, 1350, 1700, 2400, 2900, 3600].map((ms, idx) => setTimeout(() => setStage(idx + 1), ms));
    return () => timers.forEach(clearTimeout);
  }, []);
  return <div className="h-full w-full relative overflow-hidden" data-id="element-873" data-mp="1hgjgtjvjb24a">
      {/* Background: rising curve + climbing bars */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-874" data-mp="nbdv42bd2bza">
        <svg viewBox="0 0 1000 500" preserveAspectRatio="none" className="w-full h-full" aria-hidden="true" data-id="element-875" data-mp="r9v12ij1pgf4">
          {/* faint grid */}
          {[100, 200, 300, 400].map(y => <line key={y} x1="0" y1={y} x2="1000" y2={y} stroke="#e2e8f0" strokeWidth="1" data-id="element-876" data-mp="116zriujxby1q" />)}
          {/* climbing bars */}
          {[{
          x: 120,
          h: 70
        }, {
          x: 280,
          h: 130
        }, {
          x: 440,
          h: 200
        }, {
          x: 600,
          h: 290
        }, {
          x: 760,
          h: 400
        }].map((bar, idx) => <motion.rect key={bar.x} x={bar.x} width="86" rx="6" fill="#7c3aed" fillOpacity={0.05 + idx * 0.015} initial={{
          height: 0,
          y: 470
        }} animate={{
          height: stage >= 1 ? bar.h : 0,
          y: stage >= 1 ? 470 - bar.h : 470
        }} transition={{
          duration: 1.1,
          delay: idx * 0.14,
          ease: 'easeOut'
        }} data-id="element-877" data-mp="3dxk7dx0qgyjq" data-mpi={idx} />)}
          {/* rising curve */}
          <motion.path d="M 0 460 C 180 450 260 410 380 350 C 500 292 600 240 720 160 C 820 96 900 60 1000 30" fill="none" stroke="#7c3aed" strokeOpacity="0.35" strokeWidth="4" strokeLinecap="round" initial={{
          pathLength: 0
        }} animate={{
          pathLength: stage >= 1 ? 1 : 0
        }} transition={{
          duration: 2.4,
          ease: 'easeInOut'
        }} data-id="element-878" data-mp="34z8ko62zmkwk" />
        </svg>
      </div>

      <div className="relative h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-12 max-w-7xl mx-auto" data-id="element-879" data-mp="1o6bo58iafa0w">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="space-y-3 shrink-0" data-id="element-880" data-mp="1t5og27o0n7ac">
          <p className="text-teal-400 font-semibold tracking-widest uppercase text-sm" data-id="element-881" data-mp="2kv18d5he0eq1">
            The Context
          </p>
          <h2 className="text-4xl md:text-5xl font-serif text-white" data-id="element-882" data-mp="2c3ivp4jngh1p">
            The pressure is rising
          </h2>
          <p className="text-lg text-white/60" data-id="element-883" data-mp="2zsjx47u2erxh">
            Every axis of pharma marketing is expanding at once.
          </p>
        </motion.div>

        <div className="flex-1 min-h-0 flex flex-col justify-center gap-8 max-w-5xl mx-auto w-full" data-id="element-884" data-mp="34l4m8w210guo">
          {/* Rising staircase */}
          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-3 items-end pt-6" data-id="element-885" data-mp="1az5tzlue7k33">
            {PRESSURES.map((item, idx) => {
            const Icon = item.icon;
            const shown = stage >= idx + 1;
            return <motion.div key={item.label} initial={{
              opacity: 0,
              y: 40
            }} animate={{
              opacity: shown ? 1 : 0,
              y: shown ? 0 : 40
            }} transition={{
              type: 'spring',
              stiffness: 260,
              damping: 22
            }} style={{
              marginBottom: LIFT[idx]
            }} className="relative" data-id="element-886" data-mp="11zsiyoqmeng9" data-mpi={idx}>
                  <motion.div animate={shown ? {
                y: [0, -4, 0]
              } : {
                y: 0
              }} transition={{
                repeat: Infinity,
                duration: 3 + idx * 0.35,
                ease: 'easeInOut'
              }} className="bg-navy-900 border rounded-2xl p-4 flex flex-col gap-2.5" style={{
                borderColor: `rgba(124,58,237,${0.15 + idx * 0.12})`,
                boxShadow: `0 ${6 + idx * 4}px ${18 + idx * 8}px rgba(124,58,237,${0.05 + idx * 0.03})`
              }} data-id="element-887" data-mp="2dkg56kmbrqpj">
                    <div className="flex items-center justify-between" data-id="element-888" data-mp="3mmj3sojmys26">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center text-teal-400" style={{
                    backgroundColor: `rgba(124,58,237,${0.06 + idx * 0.04})`
                  }} data-id="element-889" data-mp="3mk6efqbjtff3">
                        <Icon className="w-4 h-4" data-id="element-890" data-mp="1nfep7gpqc2jw" />
                      </div>
                      <span className="text-teal-400/40 font-bold text-xs" data-id="element-891" data-mp="21ah0kjdcik43">
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                    </div>
                    <p className="text-white font-semibold text-sm leading-snug" data-id="element-892" data-mp="2b5r2z87notie">
                      {item.label}
                    </p>
                  </motion.div>
                </motion.div>;
          })}
          </div>

          {/* Consequences */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-893" data-mp="2f85knozl9n83">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: stage >= 6 ? 1 : 0,
            x: stage >= 6 ? 0 : -20
          }} className="bg-rose-500/10 border border-rose-500/30 rounded-2xl p-5" data-id="element-894" data-mp="2f6uly3l035nu">
              <h3 className="text-rose-400 font-bold text-base mb-1.5 flex items-center gap-2" data-id="element-895" data-mp="31g1a5m5jqhpf">
                <span className="w-2 h-2 rounded-full bg-rose-400" data-id="element-896" data-mp="2nomrjgfjwiiw" />
                More complexity for pharma
              </h3>
              <p className="text-white/80 leading-relaxed text-sm" data-id="element-897" data-mp="2rpgfuawwnzcb">
                Every additional brand, market and channel multiplies briefing,
                production, approval and reporting cycles.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: stage >= 7 ? 1 : 0,
            x: stage >= 7 ? 0 : 20
          }} className="bg-rose-500/10 border border-rose-500/30 rounded-2xl p-5" data-id="element-898" data-mp="24rbs9vtx5p3g">
              <h3 className="text-rose-400 font-bold text-base mb-1.5 flex items-center gap-2" data-id="element-899" data-mp="70in8i3wsyx8">
                <span className="w-2 h-2 rounded-full bg-rose-400 animate-pulse" data-id="element-900" data-mp="2qwjpgvoe91ku" />
                More fatigue for HCPs
              </h3>
              <p className="text-white/80 leading-relaxed text-sm" data-id="element-901" data-mp="2h79xdftjviwq">
                HCPs are met with more messages than ever — and pay attention to
                fewer of them than ever.
              </p>
            </motion.div>
          </div>

          {/* Payoff */}
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: stage >= 8 ? 1 : 0,
          y: stage >= 8 ? 0 : 12
        }} className="bg-teal-400/10 border border-teal-400/30 rounded-2xl px-7 py-5 text-center space-y-3" data-id="element-902" data-mp="287wmqjtowoaf">
            <p className="text-white text-xl md:text-2xl font-serif leading-snug" data-id="element-903" data-mp="3smyjk4yzcdwh">
              HCPs don't lack information — they lack time.
            </p>
            <p className="text-teal-400 font-semibold tracking-[0.25em] uppercase text-sm" data-id="element-904" data-mp="3nl94me8mzg34">
              Short · Relevant
            </p>
          </motion.div>
        </div>
      </div>
    </div>;
}